import React from 'react';
import { Badge } from "@/components/ui/badge";
import { Star, ChevronRight } from "lucide-react";
import { Course } from "@/types";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";

interface CourseCardProps {
  course: Course;
  variant?: 'upcoming' | 'completed';
}

export function CourseCard({ course, variant = 'upcoming' }: CourseCardProps) {
  const isCompleted = variant === 'completed';
  
  return (
    <Link to={`/course-player/${course.id}`} className="block">
      <div className={cn(
        "flex items-center justify-between p-4 rounded-lg transition-all duration-300 hover:scale-[1.02] cursor-pointer group",
        isCompleted 
          ? "bg-success-muted border border-success/20 hover:border-success/40" 
          : "bg-muted/50 border border-border hover:border-primary/40 hover:shadow-md"
      )}>
        <div className="flex-1">
          <h4 className="font-medium text-foreground group-hover:text-primary transition-colors">
            {course.title}
          </h4>
          <div className="flex items-center gap-2 mt-2">
            <Badge 
              variant={isCompleted ? "default" : "outline"} 
              className={cn(
                "text-xs transition-colors",
                isCompleted && "bg-success text-success-foreground"
              )}
            >
              {isCompleted ? 'Completed' : course.delivery_method?.replace('_', ' ')}
            </Badge>
            <span className="text-sm text-muted-foreground">
              {course.pd_hours} PD Hours
            </span>
          </div>
          {course.schedule?.[0] && (
            <p className="text-xs text-muted-foreground mt-1">
              {new Date(course.schedule[0].date).toLocaleDateString()}
            </p>
          )}
          {course.enrollment?.completion_date && (
            <p className="text-xs text-muted-foreground mt-1">
              Completed {new Date(course.enrollment.completion_date).toLocaleDateString()}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {course.enrollment?.evaluation_score && (
            <div className="flex items-center gap-1 text-warning">
              <Star className="w-4 h-4 fill-current" />
              <span className="text-sm font-medium">{course.enrollment.evaluation_score}</span>
            </div>
          )}
          <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
        </div>
      </div>
    </Link>
  );
}